/**
 * PropertyFile — public, anonymous-friendly property page.
 *
 * Routes:
 *   /property/:propertyId          (this file, anonymous)
 *   /account/property/:propertyId  (same file, with `signedInShell`)
 *
 * Query: ?intent=buy (default) | sell — flips which product is the
 * single primary CTA per redesign decision v1.2 item 2.
 *
 * Replaces the buyer/seller selector + role-driven flow on Home as the
 * canonical anonymous discovery surface. The post-payment Buyer's Report
 * still renders via BuyerReportViewer at /report/planning/:reportId —
 * this page is upstream of that.
 *
 * States rendered:
 *  - Loading: skeleton tree (4 rows) + skeleton CTA card.
 *  - Not found: `PropertyNotFoundError` → empty state with copy "We
 *    couldn't find public records for this address. Check the postcode,
 *    or contact us." (Item 5 in the brief — NOT a zero-row tree.)
 *  - Section unavailable (`available: false`): row renders muted "Not
 *    yet available" (handled inside DataTree).
 *  - EPC missing: row omitted entirely (handled inside DataTree).
 */

import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import type { Address, PropertyFilePreview } from '@property-intel/shared';
import { api, PropertyNotFoundError } from '../utils/api';
import DataTree from '../components/PropertyFile/DataTree';
import CTACard, { CtaIntent, Tenure } from '../components/PropertyFile/CTACard';
import ProvenanceLine from '../components/PropertyFile/ProvenanceLine';
import MobileBottomBar from '../components/PropertyFile/MobileBottomBar';
import Skeleton from '../components/ui/Skeleton';
import TermsAcceptanceModal from '../components/TermsAcceptanceModal';
import { useTermsAcceptance } from '../hooks/useTermsAcceptance';
import { useRole } from '../hooks/useRole';
import { trackEvent } from '../utils/analytics';

interface PropertyFilePageProps {
  /** When true, renders the page assuming the user is authenticated. */
  signedInShell?: boolean;
}

function formatAddress(address: Address): string {
  return [address.line1, address.line2, address.city, address.postcode]
    .filter(Boolean)
    .join(', ');
}

function parseIntent(value: string | null): CtaIntent {
  return value === 'sell' ? 'sell' : 'buy';
}

function parseTenure(value: string | null): Tenure {
  return value === 'leasehold' ? 'leasehold' : 'freehold';
}

export default function PropertyFile({ signedInShell = false }: PropertyFilePageProps) {
  const { propertyId } = useParams<{ propertyId: string }>();
  const [params] = useSearchParams();
  const location = useLocation();
  const { role } = useRole();
  // ?intent=… in the URL wins (it's how Home + cross-links navigate);
  // otherwise fall back to the cookie role so a returning owner who saved
  // /property/:id without the query param still sees the seller-flavoured
  // page.
  const queryIntent = params.get('intent');
  const intent: CtaIntent = queryIntent
    ? parseIntent(queryIntent)
    : role === 'owner'
      ? 'sell'
      : 'buy';

  // Tenure on the seller CTA is a controlled state owned by this page so
  // the choice survives navigation (it's reflected in `?tenure=…`) and
  // flows through to the Seller's Pack wizard via the same query param.
  // Default freehold — the more common case.
  const [tenure, setTenure] = useState<Tenure>(parseTenure(params.get('tenure')));

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [address, setAddress] = useState<Address | null>(null);
  const [sections, setSections] = useState<PropertyFilePreview | null>(null);

  // Buyer's Report checkout state — drives the CTACard's button on
  // intent='buy'. The seller-intent CTA stays a Link (route nav).
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const {
    showModal: showTermsModal,
    checkAcceptance,
    handleAccept,
    handleDecline,
    isAccepting,
    error: termsError,
  } = useTermsAcceptance();

  useEffect(() => {
    if (!propertyId) {
      setError('Missing property identifier.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const data = await api.getPropertyFile(propertyId);
        if (cancelled) return;
        setAddress(data.address);
        setSections(data.sections);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof PropertyNotFoundError) {
          setNotFound(true);
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load property file.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  // Sign-in returnTo points back to the signed-in property route, so the
  // user lands where they were after authenticating.
  const returnTo = `/account/property/${propertyId ?? ''}`;
  const loginHref = `/login?returnTo=${encodeURIComponent(returnTo)}`;

  // Buyer's Report: in-page Stripe checkout. Mints a planning report
  // record from the loaded address, then creates a Checkout Session,
  // then redirects to Stripe. Replaces the previous link to
  // /planning-check?address=… (which dropped users back into a search
  // form). Same terms-acceptance gate as PlanningReport.tsx.
  const startBuyerCheckout = async (): Promise<void> => {
    if (isCheckingOut || !address) return;
    setCheckoutError(null);

    const accepted = await checkAcceptance();
    if (!accepted) return; // modal will drive the rest via handleTermsAccepted

    setIsCheckingOut(true);
    try {
      trackEvent({ name: 'planning_payment_started' });
      const { reportId } = await api.createPlanningReport(address, 'anonymous-user');
      const session = await api.createCheckoutSession({
        productType: 'planning_report',
        productId: reportId,
      });
      // Page is about to unload — leave isCheckingOut true.
      window.location.assign(session.url);
    } catch (err) {
      trackEvent({
        name: 'planning_payment_failed',
        properties: { reason: err instanceof Error ? err.message : 'unknown' },
      });
      setCheckoutError(err instanceof Error ? err.message : 'Failed to start payment.');
      setIsCheckingOut(false);
    }
  };

  const handleTermsAccepted = async (): Promise<void> => {
    await handleAccept();
    setIsCheckingOut(true);
    try {
      if (!address) throw new Error('Address unavailable.');
      trackEvent({ name: 'planning_payment_started' });
      const { reportId } = await api.createPlanningReport(address, 'anonymous-user');
      const session = await api.createCheckoutSession({
        productType: 'planning_report',
        productId: reportId,
      });
      window.location.assign(session.url);
    } catch (err) {
      trackEvent({
        name: 'planning_payment_failed',
        properties: { reason: err instanceof Error ? err.message : 'unknown' },
      });
      setCheckoutError(err instanceof Error ? err.message : 'Failed to start payment.');
      setIsCheckingOut(false);
    }
  };

  // Seller's Pack: route navigation to the propertyId-anchored wizard at
  // /account/property/:propertyId/seller-pack (Phase B1, locked in
  // project-seller-flow-redesign-2026-05-29). The wizard fetches the
  // address from /api/property/:propertyId and reads tenure from the
  // ?tenure=… query param so no Step-1 form is needed.
  // Anonymous users go via /login with a returnTo that preserves the
  // exact target URL (incl. tenure) across the OTP round-trip.
  const sellerPackPath = `/account/property/${propertyId ?? ''}/seller-pack?tenure=${tenure}`;
  const primaryHrefSell = signedInShell
    ? sellerPackPath
    : `/login?returnTo=${encodeURIComponent(sellerPackPath)}`;

  const primaryLabel = intent === 'sell' ? "Start Seller's Pack — £79" : "Get Buyer's Report — £29";

  return (
    <>
      <Helmet>
        <title>
          {address ? `${address.line1}, ${address.postcode}` : 'Property file'} | UKPropertyChecks
        </title>
        <meta
          name="description"
          content="Public records for a UK property — energy performance, planning history, flood risk, and tax flags. Buyer's Report £29."
        />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <main className="bg-slate-50 min-h-screen pb-24" data-testid="property-file">
        <div className="container-main section-spacing">
          {notFound ? (
            <div className="max-w-2xl" data-testid="property-not-found">
              <h1 className="text-2xl font-semibold text-slate-950">Property not found</h1>
              <p className="mt-3 text-slate-700">
                We couldn't find public records for this address. Check the postcode, or{' '}
                <Link
                  to="/contact"
                  className="text-emerald-700 hover:text-emerald-800 underline"
                >
                  contact us
                </Link>
                .
              </p>
              <p className="mt-6">
                <Link to="/" className="text-emerald-700 hover:text-emerald-800 underline">
                  Look up another property
                </Link>
              </p>
            </div>
          ) : error ? (
            <div className="max-w-2xl" data-testid="property-error" role="alert">
              <h1 className="text-2xl font-semibold text-slate-950">Something went wrong</h1>
              <p className="mt-3 text-slate-700">{error}</p>
              <p className="mt-6">
                <Link to="/" className="text-emerald-700 hover:text-emerald-800 underline">
                  Back to homepage
                </Link>
              </p>
            </div>
          ) : (
            <>
              {/* Address + provenance */}
              <div className="max-w-3xl">
                {loading || !address ? (
                  <Skeleton className="h-7 w-2/3" />
                ) : (
                  <h1 className="text-2xl font-semibold text-slate-950">
                    {formatAddress(address)}
                  </h1>
                )}
                <ProvenanceLine />
              </div>

              {/* Data tree */}
              <div className="max-w-3xl">
                {loading || !sections ? (
                  <DataTreeSkeleton />
                ) : (
                  <DataTree sections={sections} />
                )}
              </div>

              {/* Primary CTA */}
              <div className="max-w-3xl">
                {loading ? (
                  <CtaCardSkeleton />
                ) : (
                  <>
                    {intent === 'buy' ? (
                      <CTACard
                        intent="buy"
                        onPrimaryAction={startBuyerCheckout}
                        isProcessing={isCheckingOut}
                        errorMessage={checkoutError}
                      />
                    ) : (
                      <CTACard
                        intent="sell"
                        primaryHref={primaryHrefSell}
                        tenure={tenure}
                        onTenureChange={setTenure}
                      />
                    )}
                    {/* Non-primary product as a small text link */}
                    <p className="mt-4 text-sm text-slate-700">
                      {intent === 'sell' ? (
                        <>
                          Buying instead?{' '}
                          <Link
                            to={`${location.pathname}?intent=buy`}
                            className="text-emerald-700 hover:text-emerald-800 underline"
                          >
                            Buyer's Report — £29
                          </Link>
                        </>
                      ) : (
                        <>
                          Selling this property?{' '}
                          <Link
                            to={`${location.pathname}?intent=sell`}
                            className="text-emerald-700 hover:text-emerald-800 underline"
                          >
                            Start a Seller's Pack
                          </Link>
                          {' →'}
                        </>
                      )}
                    </p>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Mobile sticky bottom bar — visible only on small screens. Save
          appears for anonymous viewers (deep-link to sign-in). */}
      {!loading && !notFound && !error && (
        intent === 'buy' ? (
          <MobileBottomBar
            primaryLabel={isCheckingOut ? 'Redirecting to Stripe…' : primaryLabel}
            onPrimaryAction={startBuyerCheckout}
            isProcessing={isCheckingOut}
            onSave={signedInShell ? undefined : () => { window.location.href = loginHref; }}
          />
        ) : (
          <MobileBottomBar
            primaryHref={primaryHrefSell}
            primaryLabel={primaryLabel}
            onSave={signedInShell ? undefined : () => { window.location.href = loginHref; }}
          />
        )
      )}

      {/* Terms acceptance modal — gates the Stripe redirect on the buy
          intent. Mirrors the modal pattern from PlanningReport.tsx. */}
      {showTermsModal && (
        <TermsAcceptanceModal
          onAccept={handleTermsAccepted}
          onDecline={handleDecline}
          isAccepting={isAccepting}
          error={termsError}
        />
      )}
    </>
  );
}

function DataTreeSkeleton() {
  return (
    <section className="mt-8" aria-label="Loading property data">
      <Skeleton className="h-5 w-1/2" />
      <ul className="mt-4 border-t border-slate-200" data-testid="datatree-skeleton">
        {[0, 1, 2, 3].map((i) => (
          <li key={i} className="border-b border-slate-200 py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-5 w-1/3" />
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-1/3" />
              </div>
              <Skeleton className="h-5 w-12" />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CtaCardSkeleton() {
  return (
    <div
      className="mt-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm space-y-3"
      role="status"
      aria-label="Loading"
    >
      <Skeleton className="h-6 w-1/3" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-11 w-48 mt-2" />
    </div>
  );
}
